import React, { useEffect, useState } from 'react';
import { XIcon, PlusIcon, TrashIcon, PlayIcon, SaveIcon, RefreshCwIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Rule } from './QualificationRules';
import { Campaign } from './CampaignCard';
import { ALL_LEADS, PoolLead } from '../data/leadPool';
import { findQualifyingLeads } from '../utils/ruleEngine';
import { LeadListTable } from './LeadListTable';
interface RuleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
  onSave: (campaignId: string, newRules: Rule[]) => void;
}
const FIELD_OPTIONS = [{
  label: 'Credit Score',
  value: 'Credit Score'
}, {
  label: 'LTV',
  value: 'LTV'
}, {
  label: 'Lead Source',
  value: 'Lead Source'
}, {
  label: 'Loan Type',
  value: 'Loan Type'
}, {
  label: 'Income',
  value: 'Income'
}, {
  label: 'Co-Borrower',
  value: 'Co-Borrower'
}, {
  label: 'Veteran Status',
  value: 'Veteran Status'
}, {
  label: 'Current Rate',
  value: 'Current Rate'
}];
const OPERATOR_OPTIONS = [{
  label: 'Equals (=)',
  value: '='
}, {
  label: 'Not Equals (!=)',
  value: '!='
}, {
  label: 'Greater Than (>)',
  value: '>'
}, {
  label: 'Greater or Equal (>=)',
  value: '>='
}, {
  label: 'Less Than (<)',
  value: '<'
}, {
  label: 'Less or Equal (<=)',
  value: '<='
}, {
  label: 'Contains',
  value: 'contains'
}];
const LOGIC_OPTIONS = [{
  label: 'AND',
  value: 'AND'
}, {
  label: 'OR',
  value: 'OR'
}];
export function RuleEditorModal({
  isOpen,
  onClose,
  campaign,
  onSave
}: RuleEditorModalProps) {
  const [rules, setRules] = useState<Rule[]>([]);
  const [simulatedLeads, setSimulatedLeads] = useState<PoolLead[]>([]);
  const [hasRunSimulation, setHasRunSimulation] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  // Initialize rules when modal opens
  useEffect(() => {
    if (isOpen && campaign) {
      setRules([...campaign.rules]);
      setSimulatedLeads([]);
      setHasRunSimulation(false);
    }
  }, [isOpen, campaign]);
  if (!isOpen) return null;
  const handleAddRule = () => {
    setRules([...rules, {
      field: 'Credit Score',
      operator: '>=',
      value: '620',
      logic: 'AND'
    }]);
    setHasRunSimulation(false);
  };
  const handleRemoveRule = (index: number) => {
    const newRules = [...rules];
    newRules.splice(index, 1);
    setRules(newRules);
    setHasRunSimulation(false);
  };
  const handleUpdateRule = (index: number, field: keyof Rule, value: string) => {
    const newRules = [...rules];
    newRules[index] = {
      ...newRules[index],
      [field]: value
    };
    setRules(newRules);
    setHasRunSimulation(false);
  };
  const runSimulation = () => {
    setIsSimulating(true);
    // Simulate network delay for realism
    setTimeout(() => {
      const qualified = findQualifyingLeads(ALL_LEADS, rules);
      setSimulatedLeads(qualified);
      setHasRunSimulation(true);
      setIsSimulating(false);
    }, 600);
  };
  const handleSave = () => {
    onSave(campaign.id, rules);
    onClose();
  };
  // Calculate stats
  const originalCount = campaign.leadsCount;
  const newCount = simulatedLeads.length;
  const diff = newCount - originalCount; // Note: This is a rough diff since original count is static mock data
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-96">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-97">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} data-id="element-98" />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-99">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full border border-gray-200" data-id="element-100">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-101">
            <div data-id="element-102">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-103">
                Edit Qualification Rules
              </h3>
              <p className="text-sm text-gray-500" data-id="element-104">
                Campaign:{' '}
                <span className="font-medium text-gray-900" data-id="element-105">
                  {campaign.name}
                </span>
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-106">
              <XIcon className="w-5 h-5" data-id="element-107" />
            </button>
          </div>

          <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)] lg:h-[600px]" data-id="element-108">
            {/* Left Panel: Rule Editor */}
            <div className="w-full lg:w-1/3 bg-gray-50 p-6 border-r border-gray-200 overflow-y-auto" data-id="element-109">
              <div className="flex items-center justify-between mb-4" data-id="element-110">
                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider" data-id="element-111">
                  Rules Configuration
                </h4>
                <Button size="sm" variant="secondary" onClick={handleAddRule} className="h-7 text-xs" data-id="element-112">
                  <PlusIcon className="w-3 h-3 mr-1" data-id="element-113" /> Add Rule
                </Button>
              </div>

              <div className="space-y-3" data-id="element-114">
                {rules.map((rule, index) => <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm relative group" data-id="element-115">
                    {index > 0 && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10" data-id="element-116">
                        <select value={rule.logic} onChange={e => handleUpdateRule(index, 'logic', e.target.value)} className="appearance-none bg-gray-100 border border-gray-300 text-gray-600 text-[10px] font-bold py-0.5 px-2 rounded-full uppercase cursor-pointer hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-117">
                          {LOGIC_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-118">
                              {opt.label}
                            </option>)}
                        </select>
                      </div>}

                    <div className="space-y-3 pt-1" data-id="element-119">
                      <Select options={FIELD_OPTIONS} value={rule.field} onChange={e => handleUpdateRule(index, 'field', e.target.value)} className="text-sm" data-id="element-120" />
                      <div className="flex gap-2" data-id="element-121">
                        <Select options={OPERATOR_OPTIONS} value={rule.operator} onChange={e => handleUpdateRule(index, 'operator', e.target.value)} className="w-1/2 text-sm" data-id="element-122" />
                        <Input value={rule.value} onChange={e => handleUpdateRule(index, 'value', e.target.value)} className="w-1/2 text-sm" placeholder="Value" data-id="element-123" />
                      </div>
                    </div>

                    <button onClick={() => handleRemoveRule(index)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-124">
                      <TrashIcon className="w-4 h-4" data-id="element-125" />
                    </button>
                  </div>)}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-126">
                <Button onClick={runSimulation} disabled={isSimulating} className="w-full justify-center" data-id="element-127">
                  {isSimulating ? <>
                      <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" data-id="element-128" />
                      Running Simulation...
                    </> : <>
                      <PlayIcon className="w-4 h-4 mr-2" data-id="element-129" />
                      Run Simulation
                    </>}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2" data-id="element-130">
                  Test your rules against the lead pool before saving.
                </p>
              </div>
            </div>

            {/* Right Panel: Simulation Results */}
            <div className="w-full lg:w-2/3 bg-white flex flex-col" data-id="element-131">
              {hasRunSimulation ? <>
                  <div className="px-6 py-4 border-b border-gray-100 bg-blue-50/30 flex items-center justify-between" data-id="element-132">
                    <div data-id="element-133">
                      <h4 className="text-sm font-bold text-gray-900" data-id="element-134">
                        Simulation Results
                      </h4>
                      <div className="flex items-center gap-2 mt-1" data-id="element-135">
                        <span className="text-xs text-gray-500" data-id="element-136">
                          Qualified Leads:{' '}
                          <span className="font-bold text-gray-900" data-id="element-137">
                            {simulatedLeads.length}
                          </span>
                        </span>
                        {/* Note: In a real app we'd compare against previous qualified set */}
                        <Badge variant="blue" data-id="element-138">Updated Count</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-139">
                      <Button variant="outline" size="sm" onClick={() => setHasRunSimulation(false)} data-id="element-140">
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden" data-id="element-141">
                    <LeadListTable leads={simulatedLeads} campaignName="Simulation Preview" data-id="element-142" />
                  </div>
                </> : <div className="flex-1 flex flex-col items-center justify-center text-center p-12 text-gray-400" data-id="element-143">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4" data-id="element-144">
                    <PlayIcon className="w-8 h-8 text-gray-300 ml-1" data-id="element-145" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-146">
                    Ready to Simulate
                  </h3>
                  <p className="max-w-sm text-sm" data-id="element-147">
                    Configure your rules on the left and click "Run Simulation"
                    to see which leads qualify from the pool.
                  </p>
                </div>}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-148">
            <Button variant="secondary" onClick={onClose} data-id="element-149">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!hasRunSimulation} data-id="element-150">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-151" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>;
}